import React from 'react'
import { Button, InputGroup, Row, ResponsiveTableRow } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../../common/DmcResponsiveTable'
import { convertUTCString } from '../../../../util/DateUtil'
import { getIdElement } from '../../../../util/StringUtil'
import { CisLink } from '../../../app/Links'
import { passThrough } from '../../../../services/toolsService'
import { actions } from './useDatsJobs'
import { RatesAction } from '../popups/RatesAction'

// I dont like the default function because mocking is weird
export default () => {}

/* eslint-disable no-underscore-dangle */

export function DatsJobsTable({ datsJobsState, datsJobsFunctions }) {
  const { t } = useTranslation()

  if (!datsJobsState?.jobsData?.jobs) {
    return null
  }

  return (
    <>
      <DatsJobsPagination datsJobsState={datsJobsState} datsJobsFunctions={datsJobsFunctions} />

      <DmcResponsiveTable
        separator={<br />}
        totalColumnCount={5}
        atSizeColumns={{
          xxl: {
            labels: [
              `${t('group.label', 'Group')} - ${t('dats.tool', 'Tool')}/${t('dats.jobId', 'Job ID')}`,
              `${t('status.label', 'Status')}/${t('success', 'Success')}/${t('dats.fail', 'Fail')}`,
              `${t('created', 'Created')}/${t('modified.label', 'Modified')}`,
              `${t('userId', 'User ID')}`,
              `${t('operations', 'Operations')}`,
            ],
            columnWidths: [3, 3, 2, 2, 2],
          },
          lg: {
            labels: [
              `${t('dats.tool', 'Tool')}`,
              `${t('status.label', 'Status')}`,
              `${t('userId', 'User ID')}`,
              `${t('operations', 'Operations')}`,
            ],
            columnWidths: [4, 4, 2, 2],
            combinationRules: [['column1'], ['column2', 'column3'], ['column4'], ['column5']],
          },
          md: {
            labels: [`${t('dats.tool', 'Tool')}`, `${t('action.details', 'Details')}`],
            columnWidths: [6, 6],
            combinationRules: [['column1'], ['column2', 'column3', 'column4', 'column5']],
          },
          default: {
            labels: [`${t('dats.jobs', 'Jobs')}`],
            columnWidths: [12],
            combinationRules: [['column1', 'column2', 'column3', 'column4', 'column5']],
          },
        }}
      >
        {/* Rows */}
        {datsJobsState.jobsData.jobs.map((job) => {
          return (
            <ResponsiveTableRow
              key={job.jobId}
              name={`dats_job_${job.jobId}`}
              column1={
                <>
                  {t(`dats.toolGroupName.${job.adminTool.groupName}`, `${job.adminTool.groupName}`)} -{' '}
                  {t(`dats.toolName.${job.adminTool.name}`, `${job.adminTool.name}`)}
                  <br />
                  {job.jobId}
                </>
              }
              column2={
                <>
                  {t(`tools.jobStatus.${job.status}`, job.status) +
                    (job.statusChangeRequest
                      ? `/ ${t(`tools.jobChangeRequestStatus.${job.statusChangeRequest}`, job.statusChangeRequest)}`
                      : '')}
                  <br />
                  {job.finishedReason && t(`tools.finishedReason.${job.finishedReason}`, job.finishedReason)}
                  <br />
                  {job.successfulItemCount != null ? job.successfulItemCount : 'No Data'} /{' '}
                  {job.failedItemCount != null ? job.failedItemCount : 'No Data'}
                </>
              }
              column3={
                <>
                  {convertUTCString(job._attributes.created, true)}
                  <br />
                  {convertUTCString(job._attributes.modified, true)}
                </>
              }
              column4={
                <>
                  {job.userId && (
                    <CisLink
                      userId={getIdElement(job.userId)}
                      userName={datsJobsState.idsMetadata[job.userId]?.contactName}
                      showId
                    />
                  )}
                </>
              }
              column5={
                <DatsJobsActions job={job} datsJobsState={datsJobsState} datsJobsFunctions={datsJobsFunctions} />
              }
            />
          )
        })}
      </DmcResponsiveTable>

      <DatsJobsPagination datsJobsState={datsJobsState} datsJobsFunctions={datsJobsFunctions} />
    </>
  )
}

export function DatsJobsActions({ job, datsJobsFunctions }) {
  const { t } = useTranslation()

  function actionOnClick(action, links) {
    if (!action.needsPayload) {
      return passThrough(links[action.linkRel].href, action.httpMethod, null, {
        'Content-Type': 'application/json',
      }).then(() => datsJobsFunctions.getTableFiltered())
    }
    return undefined
  }

  const createButtons = () => {
    const actionButtons = Object.keys(job._actions).map((actionKey) => {
      const action = job._actions[actionKey]

      if (!action.needsPayload) {
        return (
          <div key={actionKey}>
            <Button onClick={() => actionOnClick(action, job._links)}>
              {t(`dats.action.${action.buttonText}`, `${action.buttonText}`)}
            </Button>
            <br />
          </div>
        )
      }

      if (action.buttonText === 'Update Rates') {
        return (
          <div key={actionKey}>
            <RatesAction
              buttonText={t(`dats.action.${action.buttonText}`, `${action.buttonText}`)}
              path={job._links[action.linkRel].href}
              method={action.httpMethod}
              datsJob={job}
              callback={datsJobsFunctions.getTableFiltered}
            />
            <br />
          </div>
        )
      }
      return null
    })

    return [
      <div key="details">
        <Button to={`/tools/distributed/${job.jobId}?tab=details`}>{t('action.details', 'Details')}</Button>
        <br />
      </div>,
      ...actionButtons,
    ]
  }

  return <>{createButtons()}</>
}

function DatsJobsPagination({ datsJobsState, datsJobsFunctions }) {
  const { t } = useTranslation()

  if (!datsJobsState.jobsData._actions) {
    return null
  }

  function paginationOnClick(buttonAction, links) {
    return passThrough(links[buttonAction.linkRel].href, buttonAction.httpMethod, null).then((jobsData) => {
      const action = {
        type: actions.SET_JOBS_TABLE,
        payload: jobsData,
      }
      datsJobsFunctions.dispatch(action)
      return Promise.resolve()
    })
  }

  const links = datsJobsState.jobsData._links
  const first = datsJobsState.jobsData._actions.first
  const previous = datsJobsState.jobsData._actions.previous
  const next = datsJobsState.jobsData._actions.next

  return (
    <Row alignX="end">
      <InputGroup>
        <Row>
          <Button disabled={!first} onClick={() => paginationOnClick(first, links)}>
            {t(`dats.action.${first?.buttonText}`, `First`)}
          </Button>
          <Button disabled={!previous} onClick={() => paginationOnClick(previous, links)}>
            {t(`dats.action.${previous?.buttonText}`, `Previous`)}
          </Button>
          <Button disabled={!next} onClick={() => paginationOnClick(next, links)}>
            {t(`dats.action.${next?.buttonText}`, `Next`)}
          </Button>
        </Row>
      </InputGroup>
    </Row>
  )
}

/* eslint-enable no-underscore-dangle */
